// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Sort accidentals by +/- half-step changes
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions, LayoutSettings, LayoutItem } from './interactivePrototype';
import utils from '../utils';
import CheckMark from './checkMark';
import ListSorter from './listSorter';
import { toHTML } from '../uTheory.Font/font';

const settings = {
  instructions: true,
  letters: {
    checkButton: true,
    letterButtons: false,
  },
  progressTracker: true,
  checkMark: {},
};

const layout = {
  letters: { percent: 10 },
};

interface AccidentalSorterLayout extends LayoutSettings {
  sortDiv?: LayoutItem;
}

export default class AccidentalSorter extends InteractivePrototype {
  attempts: number = 0;

  listDiv: HTMLDivElement;

  listSorter!: ListSorter;

  declare layout: AccidentalSorterLayout;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    super(displayDiv, options);
    this.settings = utils.clone(settings);
    this.layout = utils.clone(layout);

    this.init(displayDiv, options);
  }

  // Overriding these methods / properties:

  init(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    // now call the prototype's init function:
    return super.init(displayDiv, options).then(() => {
      this.progressTracker!.start();
      this.letterPanel!.addCheckCallback(this.checkAnswer, this);
    });
  }

  displayInstructions() {
    this.instructionsDiv!.innerHTML =
      'Re-arrange the symbols & accidental names to match the left column.';
  }

  createCustomDivs() {
    this.listDiv = this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv!);
    this.checkMark = new CheckMark(this.listDiv);
  }

  initializeCustomElements() {
    const halfSteps = ['Raises/Lowers:', '+2 keys', '+1 key', '0 keys', '-1 key', '-2 keys'];
    const longName = [
      'Accidental Name:',
      'Double Sharp',
      'Sharp',
      'Natural',
      'Flat',
      'Double Flat',
    ];
    const symbol = [
      'Symbol:',
      { dataID: 'x', ariaLabel: 'x symbol', label: toHTML('accidental', 'x') },
      { dataID: '#', ariaLabel: 'pound/hashtag symbol', label: toHTML('accidental', '#') },
      { dataID: 'n', ariaLabel: 'natural symbol', label: toHTML('accidental', 'n') },
      { dataID: 'b', ariaLabel: 'b-like symbol', label: toHTML('accidental', 'b') },
      { dataID: 'v', ariaLabel: 'two b-like symbols', label: toHTML('accidental', 'bb') },
    ];
    const lists = [halfSteps, symbol, longName];
    this.listSorter = new ListSorter(this.listDiv, lists);
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  gameOver() {
    this.lettersDiv!.style.display = 'none';
    this.listSorter?.gameOver();
    super.gameOver();
  }

  checkAnswer() {
    this.attempts++;
    const answers = this.listSorter.check();
    const gameOver = answers.wrong === 0;
    this.progressTracker!.multipleAnswers(
      [
        {
          skill: 'accidental_types',
          right: answers.right,
          wrong: answers.wrong,
          questions: answers.right + answers.wrong,
        },
      ],
      answers.right + answers.wrong,
      gameOver
    );
    if (gameOver) {
      this.checkMark!.check(2);
      this.gameOver();
    } else {
      this.checkMark!.x();
      if (this.attempts > 1) this.listSorter.hint();
    }
  }

  static registry = {
    description: 'Match accidental symbols and names',
    skills: ['accidental_types'],
  };
}
